import { useCallback } from 'react';

import { useFormContext } from 'react-hook-form';

import { parseISO } from 'date-fns';

import { useDidMount } from '@hsp/react-hooks';
import { isDefined, isNotDefined, isOfType } from '@hsp/utilities';

import { useSaveStoreSet } from 'pages/audience/shared/save/hooks/context';
import { AudienceCategory, AudienceOrderType, IAudience } from 'types/api/audience';
import { ISaveProps, SaveFeatures, SaveForm } from 'types/pages/audience-builder/shared/save';

export const useSaveButtonConfiguration = ({ features, onOpen }: Pick<ISaveProps, 'features' | 'onOpen'>) => {
  const setSaveStore = useSaveStoreSet();

  const { reset } = useFormContext<SaveForm>();

  const onClick = useCallback(
    (
      options: { saveAsMaster?: boolean; saveAsPreBuilt?: boolean; saveTo?: boolean } = {
        saveAsMaster: false,
        saveAsPreBuilt: false,
        saveTo: false,
      }
    ) => {
      const { saveAsMaster, saveAsPreBuilt, saveTo } = options;

      if (isDefined(onOpen)) {
        onOpen().then((data) => {
          if (typeof data === 'boolean') {
            if (data) {
              setSaveStore({ isOpen: true, saveTo });
            }
          } else {
            setSaveStore({ data, isOpen: true, saveTo });

            if (isNotDefined(data)) {
              const base = {
                client: undefined,
                giftDateEnd: undefined,
                giftDateStart: undefined,
                id: undefined,
                name: undefined,
                orderNumber: undefined,
              };

              if (saveAsMaster) {
                reset({
                  ...base,
                  categoryId: AudienceCategory.MasterFile,
                  type: 'master-file',
                });
                setSaveStore({ features: SaveFeatures.ShopMasterFile, formType: 'master-file' });

                return;
              }

              if (saveAsPreBuilt) {
                reset({
                  ...base,
                  categoryId: AudienceCategory.PreBuilt,
                  orderNumber: '',
                  type: 'pre-built',
                });
                setSaveStore({ features: SaveFeatures.ShopPreBuilt, formType: 'pre-built' });

                return;
              }

              reset({
                ...base,
                categoryId: AudienceCategory.Basic,
                description: '',
                mailCode: '',
                sequenceNumber: 1,
                tags: [],
                type: 'basic',
              });
              setSaveStore({ features: SaveFeatures.Shop, formType: 'basic' });

              return;
            }

            const { clientId, clientName, giftDateEnd, giftDateStart, id: audienceUid, orderNumber, name } = data;
            const base = {
              client: isDefined(clientId) && isDefined(clientName) ? { id: clientId, name: clientName } : undefined,
              giftDateEnd: isDefined(giftDateEnd) ? parseISO(giftDateEnd.toString()) : undefined,
              giftDateStart: isDefined(giftDateStart) ? parseISO(giftDateStart.toString()) : undefined,
              id: audienceUid,
              name,
              orderNumber: orderNumber ?? undefined,
            };

            if (isOfType<IAudience>(data, 'category')) {
              const { category, orderType } = data;
              const { id: categoryId } = category ?? { id: AudienceCategory.Basic };

              switch (categoryId) {
                case AudienceCategory.PreBuilt:
                  reset({
                    ...base,
                    orderType: isNotDefined(orderType) ? undefined : { id: orderType?.id, name: orderType?.name },
                    type: 'audience-pre-built',
                  });
                  setSaveStore({ features: SaveFeatures.AudiencePreBuilt, formType: 'audience-pre-built' });
                  break;

                case AudienceCategory.MasterFile:
                  reset({
                    ...base,
                    orderType: isNotDefined(orderType) ? undefined : { id: orderType?.id, name: orderType?.name },
                    type: 'audience-master-file',
                  });
                  setSaveStore({ features: SaveFeatures.AudienceMasterFile, formType: 'audience-master-file' });
                  break;

                default:
                  reset({
                    ...base,
                    orderType: isNotDefined(orderType) ? undefined : { id: orderType?.id, name: orderType?.name },
                    type: 'audience',
                  });
                  setSaveStore({ features: SaveFeatures.Audience, formType: 'audience' });
                  break;
              }

              return;
            }

            const {
              description,
              mailCode,
              orderTypeId,
              primarySelect,
              secondarySelect,
              sequenceNumber,
              shopId,
              statusId,
              tags,
            } = data;
            let { categoryId = AudienceCategory.Basic } = data;

            if (saveAsMaster) {
              base.id = undefined;
              categoryId = AudienceCategory.MasterFile;
            }

            if (saveAsPreBuilt) {
              base.id = undefined;
              categoryId = AudienceCategory.PreBuilt;
            }

            setSaveStore({ data: { ...data, categoryId } });

            switch (categoryId) {
              case AudienceCategory.PreBuilt:
                reset({
                  ...base,
                  categoryId,
                  description: description ?? '',
                  mailCode: mailCode ?? '',
                  orderTypeId: orderTypeId ?? AudienceOrderType.NotSet,
                  primarySelect,
                  secondarySelect,
                  sequenceNumber,
                  shopId,
                  statusId,
                  tags,
                  type: 'pre-built',
                });
                setSaveStore({ features: SaveFeatures.ShopPreBuilt, formType: 'pre-built' });
                break;

              case AudienceCategory.MasterFile:
                reset({
                  ...base,
                  categoryId,
                  description: description ?? '',
                  orderTypeId: orderTypeId ?? AudienceOrderType.NotSet,
                  primarySelect,
                  secondarySelect,
                  sequenceNumber,
                  shopId,
                  statusId,
                  tags,
                  type: 'master-file',
                });
                setSaveStore({ features: SaveFeatures.ShopMasterFile, formType: 'master-file' });
                break;

              default:
                reset({
                  ...base,
                  categoryId,
                  description: description ?? '',
                  mailCode: mailCode ?? '',
                  orderTypeId: orderTypeId ?? AudienceOrderType.NotSet,
                  primarySelect,
                  secondarySelect,
                  sequenceNumber,
                  shopId,
                  statusId,
                  tags,
                  type: 'basic',
                });
                setSaveStore({ features: SaveFeatures.Shop, formType: 'basic' });
                break;
            }
          }
        });

        return;
      }

      setSaveStore({ isOpen: true });
    },
    [onOpen, reset, setSaveStore]
  );

  useDidMount(() => setSaveStore({ features: features ?? SaveFeatures.Shop }));

  return {
    onClick,
  };
};
